import moment from 'moment';

import { COMPARISON, DATA_STATE, SEARCH_TYPES } from './period';

/**
 * A GSC tool report's scope, carried in the page's own address.
 *
 * A report here is a question about one property over one window, and the answer is worth coming
 * back to tomorrow or sending to someone. Keeping the applied scope in the query string makes the
 * address the report: a reload, a bookmark or a pasted link opens the same figures rather than an
 * empty scope bar, and a report found in a URL is run on arrival instead of waiting behind a
 * click the user has already made once.
 *
 * Only an *applied* scope is written, never the draft, so the address always names figures that
 * were actually fetched and an abandoned edit leaves no trace. Writing replaces the current
 * history entry rather than pushing one: Back leaves the report instead of walking back through
 * every filter the user tried on the way to it.
 *
 * Everything read back is validated. A query string is typed by hand as easily as it is copied,
 * and a value that is not one this report recognises is dropped in favour of the default rather
 * than sent to Search Console.
 */

/** The query-string keys this module owns. Anything else on the URL is left untouched. */
export const SCOPE_KEYS = [
    'site',
    'type',
    'country',
    'device',
    'start',
    'end',
    'compare',
    'baseline-start',
    'baseline-end',
    'preliminary',
];

const DATE_FORMAT = 'YYYY-MM-DD';

/** A calendar date exactly as this report writes them, or null for anything else. */
function readDate(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    return moment(value, DATE_FORMAT, true).isValid() ? value : null;
}

/** The current query string as a plain object. */
export function readParams(search = window.location.search) {
    const params = {};
    new URLSearchParams(search).forEach((value, key) => {
        params[key] = value;
    });
    return params;
}

/** Whether the address carries a scope at all — which is what makes a report auto-run. */
export function hasScope(params, extraKeys = []) {
    return [...SCOPE_KEYS, ...extraKeys].some((key) => !!params[key]);
}

/** The shared part of a draft, as far as the address describes it. */
export function scopeDraftFromParams(params) {
    const draft = {};

    if (params.site) draft.siteUrl = params.site;
    if (SEARCH_TYPES.includes(params.type)) draft.searchType = params.type;
    if (params.country) draft.country = params.country.toLowerCase();
    if (params.device) draft.device = params.device.toUpperCase();

    const start = readDate(params.start);
    const end = readDate(params.end);
    if (start) draft.startDate = start;
    if (end) draft.endDate = end;

    if (Object.values(COMPARISON).includes(params.compare)) draft.comparison = params.compare;

    const baselineStart = readDate(params['baseline-start']);
    const baselineEnd = readDate(params['baseline-end']);
    if (baselineStart && baselineEnd) {
        draft.customBaseline = { startDate: baselineStart, endDate: baselineEnd };
    }

    if (params.preliminary === '1') draft.includePreliminary = true;

    return draft;
}

/**
 * The shared part of an applied scope, as query parameters.
 *
 * It is built from the resolved scope rather than the draft so the address describes what was
 * fetched: a custom baseline is written as the dates it settled on, and the data state as the
 * flag that produced it.
 */
export function scopeParamsFromScope(scope) {
    const params = {
        site: scope.siteUrl,
        type: scope.searchType,
        start: scope.current?.startDate,
        end: scope.current?.endDate,
        compare: scope.comparison,
        country: scope.country,
        device: scope.device,
    };

    if (scope.comparison === COMPARISON.CUSTOM && scope.baseline) {
        params['baseline-start'] = scope.baseline.startDate;
        params['baseline-end'] = scope.baseline.endDate;
    }

    if (scope.dataState === DATA_STATE.ALL) params.preliminary = '1';

    return params;
}

/**
 * Puts `params` on the address, clearing the keys this module owns and leaving every other
 * parameter the page was opened with in place.
 */
export function writeParams(params, ownedKeys = SCOPE_KEYS) {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;

    const url = new URL(window.location.href);
    ownedKeys.forEach((key) => url.searchParams.delete(key));

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        url.searchParams.set(key, value);
    });

    window.history.replaceState(window.history.state, '', url.toString());
}
